<template>
  <div class="p-6 max-w-7xl mx-auto space-y-8">

    <!-- HEADER -->
    <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
      <div>
        <h1 class="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
          <CreditCard class="w-7 h-7 text-brand-500" />
          Suscripción & Facturación
        </h1>
        <p class="text-sm text-slate-500 dark:text-slate-400">
          Gestiona tu plan comercial, límites de IA y reportes de transferencias bancarias.
        </p>
      </div>

      <!-- Badge de Estado General -->
      <div class="flex items-center gap-2">
        <span
          class="px-3 py-1.5 rounded-xl text-xs font-bold border flex items-center gap-1.5"
          :class="subscriptionStatusBadge"
        >
          <span class="w-2 h-2 rounded-full" :class="statusDotColor"></span>
          {{ subscriptionStatusLabel }}
        </span>
      </div>
    </div>

    <!-- SECCIÓN DE PLANES -->
    <div class="space-y-4">
      <div class="border-b border-slate-200 dark:border-slate-800 pb-3 flex justify-between items-end">
        <div>
          <h2 class="text-lg font-bold text-slate-900 dark:text-white">
            {{ isNewUser ? 'Selecciona tu Plan para iniciar los 3 Días de Prueba' : 'Planes de Suscripción Disponibles' }}
          </h2>
          <p class="text-xs text-slate-500">
            {{ isNewUser ? 'Elige la capacidad que necesitas. Durante los 3 días de trial no pagarás nada.' : 'Puedes mejorar tu plan en cualquier momento para obtener más instancias y productos.' }}
          </p>
        </div>
      </div>

      <!-- Grid de Planes -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div
          v-for="plan in plans"
          :key="plan.id"
          class="bg-white dark:bg-slate-800 border rounded-3xl p-6 flex flex-col justify-between relative transition-all shadow-xs"
          :class="[
            user.current_plan_id === plan.id
              ? 'border-brand-500 ring-2 ring-brand-500/20 shadow-md'
              : 'border-slate-200 dark:border-slate-700/80 hover:border-slate-300'
          ]"
        >
          <div v-if="user.current_plan_id === plan.id" class="absolute -top-3 right-6 bg-brand-500 text-white text-[10px] font-bold uppercase tracking-wider px-3 py-0.5 rounded-full shadow-xs">
            Plan Actual
          </div>

          <div class="space-y-4">
            <div>
              <h3 class="text-base font-bold text-slate-900 dark:text-white">{{ plan.name }}</h3>
              <p v-if="plan.description" class="text-xs text-slate-500 mt-0.5 line-clamp-2">{{ plan.description }}</p>
              <div class="flex items-baseline gap-1 mt-2">
                <span class="text-3xl font-black font-mono text-slate-900 dark:text-white">${{ plan.price }}</span>
                <span class="text-xs text-slate-400 font-semibold">USD /mes</span>
              </div>
            </div>

            <!-- Features dinámicas según tu tabla plans -->
            <div class="space-y-2.5 pt-4 border-t border-slate-100 dark:border-slate-700 text-xs">
              <div class="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                <Check class="w-4 h-4 text-emerald-500 shrink-0" />
                <span>Hasta <strong>{{ plan.max_instances }} Línea(s) / Instancia(s) WA</strong></span>
              </div>
              <div class="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                <Check class="w-4 h-4 text-emerald-500 shrink-0" />
                <span>Catálogo de hasta <strong>{{ plan.max_products }} Productos/Servicios</strong></span>
              </div>
              <div class="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                <Check class="w-4 h-4 text-emerald-500 shrink-0" />
                <span>Hasta <strong>{{ plan.max_rules }} Reglas</strong> de comportamiento</span>
              </div>
              <div class="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                <Check class="w-4 h-4 text-emerald-500 shrink-0" />
                <span>Respuestas IA de hasta <strong>{{ plan.max_words_response }} palabras</strong></span>
              </div>
            </div>
          </div>

          <!-- Botones de Acción -->
          <div class="pt-6">
            <button
              v-if="isNewUser"
              @click="selectInitialPlan(plan.id)"
              :disabled="isProcessing"
              class="w-full py-2.5 rounded-xl bg-brand-500 hover:bg-brand-600 text-white font-bold text-xs transition-all shadow-sm flex items-center justify-center gap-2"
            >
              <Zap class="w-3.5 h-3.5" /> Activar 3 Días de Demo
            </button>

            <div
              v-else-if="user.current_plan_id === plan.id"
              class="w-full py-2.5 rounded-xl bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 font-bold text-xs text-center"
            >
              Plan Activo
            </div>

            <button
              v-else-if="parseFloat(plan.price) > parseFloat(user.current_plan_price || 0)"
              @click="requestUpgrade(plan.id)"
              :disabled="isProcessing"
              class="w-full py-2.5 rounded-xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 hover:bg-brand-500 dark:hover:bg-brand-500 dark:hover:text-white font-bold text-xs transition-all flex items-center justify-center gap-2"
            >
              <ArrowUpRight class="w-3.5 h-3.5" /> Mejorar a este Plan ($USD)
            </button>

            <button
              v-else
              disabled
              class="w-full py-2.5 rounded-xl bg-slate-50 dark:bg-slate-800/40 text-slate-400 dark:text-slate-600 font-semibold text-xs cursor-not-allowed border border-dashed border-slate-200 dark:border-slate-700"
            >
              No disponible (Inferior)
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- SECCIÓN DE FACTURAS Y DATOS DE PAGO -->
    <div v-if="!isNewUser" class="space-y-6">

      <!-- Cuentas Bancarias en Dólares -->
      <div class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700/80 rounded-3xl p-6 space-y-4">
        <h3 class="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2">
            <Building2 class="w-4 h-4 text-brand-500" /> Cuentas Disponibles para Transferencia Directa ($ USD)
        </h3>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
            <div
            v-for="method in paymentMethods"
            :key="method.id"
            class="p-4 rounded-2xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700/60"
            >
            <span class="font-bold text-slate-800 dark:text-slate-200 block">{{ method.institution_name }}</span>
            <p class="font-mono text-emerald-600 dark:text-emerald-400 font-black text-sm mt-1">{{ method.account_number_or_name }}</p>
            <p class="text-[11px] text-slate-500 mt-0.5">{{ method.additional_details }}</p>
            </div>
        </div>
        </div>

      <!-- Tabla de Facturas -->
      <div class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700/80 rounded-3xl p-6 space-y-4">
        <h3 class="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2">
          <FileText class="w-4 h-4 text-brand-500" /> Historial de Facturación ($ USD)
        </h3>

        <div v-if="invoices.length === 0" class="text-center py-8 text-xs text-slate-400">
          No tienes facturas emitidas por el momento.
        </div>

        <div v-else class="overflow-x-auto">
          <table class="w-full text-left text-xs">
            <thead>
              <tr class="text-slate-400 border-b border-slate-100 dark:border-slate-700 uppercase tracking-wider font-bold">
                <th class="pb-3">Factura</th>
                <th class="pb-3">Plan</th>
                <th class="pb-3">Período</th>
                <th class="pb-3">Total (USD)</th>
                <th class="pb-3">Estado</th>
                <th class="pb-3 text-right">Comprobante</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100 dark:divide-slate-700 font-medium">
              <tr v-for="inv in invoices" :key="inv.id" class="hover:bg-slate-50 dark:hover:bg-slate-700/30">
                <td class="py-3 font-bold text-slate-900 dark:text-white font-mono">#INV-{{ inv.id }}</td>
                <td class="py-3 font-semibold text-slate-700 dark:text-slate-300">{{ inv.plan_name }}</td>
                <td class="py-3 text-slate-500">{{ inv.period_start }} al {{ inv.period_end }}</td>
                <td class="py-3 font-black font-mono text-slate-900 dark:text-white">${{ inv.amount_usd }}</td>
                <td class="py-3">
                  <span
                    class="px-2.5 py-0.5 rounded-full text-[10px] font-bold"
                    :class="[
                      inv.status === 'paid' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400' :
                      inv.proof_file_url ? 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400' :
                      'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400'
                    ]"
                  >
                    {{ inv.status === 'paid' ? 'Pagada' : (inv.proof_file_url ? 'En Verificación' : 'Pendiente') }}
                  </span>
                </td>
                <td class="py-3 text-right">
                  <!-- Si está pagada -->
                  <span v-if="inv.status === 'paid'" class="text-emerald-600 font-bold text-xs flex items-center justify-end gap-1">
                    <Check class="w-3.5 h-3.5" /> Pagado
                  </span>

                  <!-- Si está pendiente (Botones de Pago) -->
                  <div v-else class="flex items-center justify-end gap-2">
                    <!-- Botón de Pago Manual (Tu código actual) -->
                    <button
                      @click="openUploadModal(inv)"
                      class="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600 font-bold text-[11px] transition-all"
                    >
                      {{ inv.proof_file_url ? 'Reemplazar' : 'Transferencia' }}
                    </button>

                    <!-- NUEVO: Botón de PayPal -->
                    <button
                      @click="payWithPayPal(inv.id)"
                      :disabled="isProcessingPayPal === inv.id"
                      class="px-3 py-1.5 rounded-xl bg-[#0070BA] hover:bg-[#005ea6] text-white font-bold text-[11px] transition-all shadow-xs flex items-center gap-1.5 disabled:opacity-50"
                    >
                      <span v-if="isProcessingPayPal === inv.id">Cargando...</span>
                      <span v-else>Pagar con PayPal</span>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

    </div>

    <!-- MODAL PARA SUBIR COMPROBANTE -->
    <div v-if="isUploadModalOpen" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm animate-fade-in">
      <div class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 w-full max-w-md space-y-4 shadow-2xl">
        <div class="flex justify-between items-center border-b border-slate-100 dark:border-slate-800 pb-3">
          <h3 class="text-sm font-bold text-slate-900 dark:text-white">
            Reportar Pago: Factura #INV-{{ selectedInvoice.id }}
          </h3>
          <button @click="isUploadModalOpen = false" class="text-slate-400 hover:text-slate-600">
            <X class="w-4 h-4" />
          </button>
        </div>

        <div class="space-y-3 text-xs">
          <div>
            <label class="font-bold text-slate-700 dark:text-slate-300 block mb-1">Método / Banco Utilizado</label>
            <select v-model="formPayment.method" class="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-transparent text-slate-900 dark:text-white font-semibold">
                <option
                v-for="method in paymentMethods"
                :key="method.code"
                :value="method.code"
                >
                {{ method.institution_name }}
                </option>
            </select>
            </div>

          <div>
            <label class="font-bold text-slate-700 dark:text-slate-300 block mb-1">Número de Referencia / Minuta</label>
            <input
              type="text"
              v-model="formPayment.reference"
              placeholder="Ej. REF-98341029"
              class="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-transparent text-slate-900 dark:text-white font-semibold outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>

          <div>
            <label class="font-bold text-slate-700 dark:text-slate-300 block mb-1">Foto o Captura del Comprobante</label>
            <input
              type="file"
              @change="handleFileChange"
              accept="image/*,.pdf"
              class="w-full text-slate-500 file:mr-2 file:py-1.5 file:px-3 file:rounded-xl file:border-0 file:bg-brand-500 file:text-white file:font-bold file:text-xs"
            />
          </div>
        </div>

        <div class="flex justify-end gap-2 pt-3 border-t border-slate-100 dark:border-slate-800">
          <button @click="isUploadModalOpen = false" class="px-4 py-2 rounded-xl text-xs font-bold text-slate-500">
            Cancelar
          </button>
          <button
            @click="submitPaymentProof"
            :disabled="isSubmitting"
            class="px-4 py-2 rounded-xl bg-brand-500 hover:bg-brand-600 text-white text-xs font-bold transition-all shadow-xs"
          >
            {{ isSubmitting ? 'Enviando...' : 'Confirmar y Enviar' }}
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { CreditCard, Zap, Check, ArrowUpRight, Building2, FileText, X } from 'lucide-vue-next';
import apiClient from '@/api/client';
import { useToast } from '@/composables/useToast';
import { SecureStorage } from '@/utils/secureStorage';
const { showSuccess, showError } = useToast();

const user = ref({});
const plans = ref([]);
const invoices = ref([]);
const paymentMethods = ref([]);

const isProcessing = ref(false);
const isUploadModalOpen = ref(false);
const selectedInvoice = ref({});
const isSubmitting = ref(false);

const formPayment = ref({
  method: 'BAC',
  reference: '',
  file: null
});

const fetchBillingData = async () => {
  try {
    const localUser = SecureStorage.getItem('neurals_user');
    const userId = localUser?.id || 0;

    const res = await apiClient.get('/get_plans_and_billing', {
      params: { user_id: userId }
    });

    if (res.data?.data) {
      user.value = res.data.data.user || {};
      plans.value = res.data.data.plans || [];
      invoices.value = res.data.data.invoices || [];
      paymentMethods.value = res.data.data.payment_methods || [];

      // Sincronizar almacenamiento local con la respuesta de la BD
      if (localUser && user.value.subscription_status) {
        SecureStorage.setItem('neurals_user', {
          ...localUser,
          is_premium: user.value.current_plan_id,
          subscription_status: user.value.subscription_status,
          subscription_expires_at: user.value.subscription_expires_at,
          trial_ends_at: user.value.trial_ends_at
        });
      }
    }
  } catch (err) {
    showError('Error al sincronizar datos de facturación.');
  }
};

const isNewUser = computed(() => {
  return !user.value.current_plan_id || parseInt(user.value.current_plan_id) === 0;
});

const selectInitialPlan = async (planId) => {
  isProcessing.value = true;
  try {
    const user = SecureStorage.getItem('neurals_user')
    const formData = new FormData();
    formData.append('plan_id', planId);
    formData.append('user_id', user.id);
    const res = await apiClient.post('/select_initial_plan', formData);
    showSuccess(res.data?.message || 'Plan activado');
    await fetchBillingData();
  } catch (err) {
    console.log(err)
    showError('No se pudo activar el plan inicial.');
  } finally {
    isProcessing.value = false;
  }
};

const requestUpgrade = async (newPlanId) => {
  if (!confirm('¿Deseas mejorar a este plan? Se generará la factura en USD correspondiente.')) return;

  isProcessing.value = true;
  try {
    const formData = new FormData();
    formData.append('new_plan_id', newPlanId);
    const res = await apiClient.post('/upgrade_plan', formData);
    showSuccess(res.data?.message);
    await fetchBillingData();
  } catch (err) {
    showError(err.response?.data?.message || 'No se pudo realizar el cambio de plan.');
  } finally {
    isProcessing.value = false;
  }
};

const openUploadModal = (inv) => {
  selectedInvoice.value = inv;
  formPayment.value.reference = inv.payment_reference || '';
  formPayment.value.method = inv.payment_method || (paymentMethods.value.length > 0 ? paymentMethods.value[0].code : '');
  formPayment.value.file = null;
  isUploadModalOpen.value = true;
};

const handleFileChange = (e) => {
  formPayment.value.file = e.target.files[0];
};

const submitPaymentProof = async () => {
  if (!formPayment.value.reference) {
    showError('Por favor ingresa el número de referencia.');
    return;
  }

  isSubmitting.value = true;

  const localUser = SecureStorage.getItem('neurals_user') || SecureStorage.getItem('llevo_user');
  const userId = localUser?.id || user.value?.id || 0;

  const formData = new FormData();
  formData.append('user_id', String(userId));
  formData.append('invoice_id', String(selectedInvoice.value.id));
  formData.append('payment_method', formPayment.value.method);
  formData.append('payment_reference', formPayment.value.reference);

  // Adjuntar el archivo binario
  if (formPayment.value.file instanceof File) {
    formData.append('proof', formPayment.value.file, formPayment.value.file.name);
  }

  try {
    const res = await apiClient.post('/submit_payment', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      transformRequest: [(data) => data] // Evita que interceptores de Axios conviertan el FormData a JSON
    });

    showSuccess(res.data?.message || 'Comprobante recibido.');
    isUploadModalOpen.value = false;
    await fetchBillingData();
  } catch (err) {
    showError('Error al subir el comprobante.');
  } finally {
    isSubmitting.value = false;
  }
};

const subscriptionStatusLabel = computed(() => {
  const map = {
    trial: 'Prueba Gratuita (3 Días)',
    active: 'Suscripción Activa',
    suspended: 'Cuenta Pausada / Vencida'
  };
  return map[user.value.subscription_status] || 'Sin Plan Asignado';
});

const subscriptionStatusBadge = computed(() => {
  if (user.value.subscription_status === 'active') {
    return 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-800';
  }
  if (user.value.subscription_status === 'trial') {
    return 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-400 dark:border-blue-800';
  }
  return 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-400 dark:border-red-800';
});

const statusDotColor = computed(() => {
  if (user.value.subscription_status === 'active') return 'bg-emerald-500';
  if (user.value.subscription_status === 'trial') return 'bg-blue-500';
  return 'bg-red-500';
});

// Agrega esta variable de estado junto a tus otros refs
const isProcessingPayPal = ref(null);

// Función para solicitar el link de pago y redirigir
const payWithPayPal = async (invoiceId) => {
  isProcessingPayPal.value = invoiceId;
  try {
    const formData = new FormData();
    formData.append('invoice_id', invoiceId);

    // Llama al endpoint de tu index.php
    const res = await apiClient.post('/create_paypal_order', formData);

    if (res.data?.status === 'success' && res.data?.approve_link) {
      // Redirige la ventana a la pantalla de pago de PayPal
      window.location.href = res.data.approve_link;
    } else {
      showError('No se pudo generar el enlace de pago.');
    }
  } catch (err) {
    showError(err.response?.data?.message || 'Error al conectar con PayPal.');
  } finally {
    isProcessingPayPal.value = null;
  }
};
onMounted(() => {
  fetchBillingData();
});
</script>